import { useCallback, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import * as styles from "./styles.module.scss";
import CalendarToolbar from "./toolbar";
import { Event, customEventStyling } from "./event";
import ShowMorePopper from "./show-more-popper";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { cloneDeep } from "lodash";
import { isSameDay } from "date-fns";
import { Typography } from "@mui/material";

// Setup the localizer by providing the moment Object
// to the correct localizer.
export const localizer = momentLocalizer(moment); // or globalizeLocalizer
const MAX_EVENTS_PER_SLOT = 1;
const DragAndDropCalendar = withDragAndDrop(Calendar);

const UICalendar = (props) => {
  const {
    events = [],
    customEventTile = undefined,
    options = {},
    showAvatar = false,
    stopDraggable,
    selectedView = "month",
    handleView = (view) => console.log("Calendar view::", view),
    views = { day: true, week: true, month: true },
    removeTime = false,
    handleRange = (e) => {
      console.log(e);
    },
    onHover = false,
    popOverComp,
    emptySlotMessage,
    defaultView = "month",
    eventStyling = customEventStyling,
    showContextMenu = true,
    grouping = false,
    onShowMoreLoadEvents,
    weekDayTaskState,
    onLoadMoreWeekDayTasks,
    ...rest
  } = props;
  // for changing toolbar views buttons text

  const calendarRef = useRef(null);

  const groupOverlappingEvents = (events) => {
    const grouped = [];

    events.forEach((event) => {
      const overlap = grouped.find(
        (e) =>
          e.start < event.end &&
          event.start < e.end &&
          event?.serviceType == e?.serviceType,
      );

      if (overlap) {
        overlap._overlaps = overlap._overlaps || [];
        overlap._overlaps.push(event);
      } else {
        grouped.push({ ...event, _overlaps: [] });
      }
    });

    return grouped;
  };

  const messages = useMemo(
    () => ({
      day: "Day",
      week: "Week",
      month: "Month",
      showMore: (total, remainingEvents, events) => {
        const dayTotalCount = events?.[0]?.dayTotalCount;
        const computedMore =
          typeof dayTotalCount === "number"
            ? Math.max(dayTotalCount - MAX_EVENTS_PER_SLOT, total)
            : total;
        return (
          <ShowMorePopper
            title={`+${computedMore} more`}
            events={events}
            dayDate={
              remainingEvents instanceof Date
                ? remainingEvents
                : events?.[0]?.start
            }
            onLoadDayEvents={onShowMoreLoadEvents}
            contextMenuOptions={options?.contextMenuOptions}
            popOverComp={popOverComp}
            onHover={onHover}
            onSelectEvent={rest?.onSelectEvent}
          />
        );
      },
    }),
    [onHover, onShowMoreLoadEvents, options, popOverComp, rest?.onSelectEvent],
  );
  const scrollToCenter = (container, element) => {
    if (!container || !element) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const scrollTop =
      elementRect.top -
      containerRect.top +
      container.scrollTop -
      containerRect.height / 2 +
      elementRect.height / 2;

    container.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  };
  const scrollEventToCenter = (eventEl) => {
    const vContainer = document.querySelector(".rbc-time-content"); // vertical
    const hContainer = document.querySelector(".rbc-time-view"); // horizontal

    if (!eventEl || !vContainer || !hContainer) return;

    const eRect = eventEl.getBoundingClientRect();
    const vRect = vContainer.getBoundingClientRect();
    const hRect = hContainer.getBoundingClientRect();

    // ---- Vertical center ----
    const targetTop =
      eRect.top -
      vRect.top +
      vContainer.scrollTop -
      vRect.height / 2 +
      eRect.height / 2;

    // ---- Horizontal center ----
    const targetLeft =
      eRect.left -
      hRect.left +
      hContainer.scrollLeft -
      hRect.width / 2 +
      eRect.width / 2;

    vContainer.scrollTo({
      top: targetTop,
      left: targetLeft + 200,
      behavior: "smooth",
    });
  };

  const scrollToFirstEvent = () => {
    if (!calendarRef.current) return;

    // Find all event nodes in the DOM
    const eventNodes = calendarRef.current.querySelectorAll(".rbc-event");

    if (!eventNodes || eventNodes.length === 0) return;

    const firstEvent = eventNodes[0];
    scrollEventToCenter(firstEvent);
    // firstEvent.scrollIntoView({
    //   behavior: "smooth",
    //   block: "center", // or "center"
    //   inline: "nearest",
    // });
  };

  useEffect(() => {
    if (rest?.date) setTimeout(scrollToFirstEvent, 1000);
  }, [(rest?.date || "").toString(), selectedView]);

  // added custom toolbar and custom event component
  const components = useMemo(
    () => ({
      toolbar: (props) => {
        const cloendProps = cloneDeep(props);
        const currentDate = moment(cloendProps?.date);
        if (cloendProps.view === Views.DAY) {
          cloendProps.label = currentDate.format("dddd, MMM D, YYYY");
        } else if (moment(cloendProps.label, "MMMM D – D", true).isValid()) {
          const [startDate, endDate] = cloendProps.label.split(" – ");
          cloendProps.label = `${moment(startDate, "MMMM D").format("MMM D")}-${endDate} ${moment(cloendProps.date).format(", YYYY")}`;
        } else if (moment(cloendProps.label, "dddd MMM D", true).isValid()) {
          const date = moment(cloendProps.label, "dddd MMM D", true);
          cloendProps.label = `${date.format("MMM D, YYYY")}`;
        } else if (moment(cloendProps.label, "dddd MMM DD", true).isValid()) {
          const date = moment(cloendProps.label, "dddd MMM DD", true);
          cloendProps.label = `${date.format("MMM D, YYYY")}`;
        } else if (moment(cloendProps.label, "MMMM YYYY", true).isValid()) {
          cloendProps.label = moment(cloendProps.label, "MMMM YYYY").format(
            "MMM YYYY",
          );
        } else if (
          moment(cloendProps.label, "MMMM DD – MMMM DD", true).isValid()
        ) {
          const [startDate, endDate] = cloendProps.label.split(" – ");
          cloendProps.label = `${moment(startDate, "MMMM DD").format("MMM D")}-${moment(endDate, "MMMM DD").format(" MMM D")}`;
        } else if (moment(cloendProps.label, "MMMM DD – DD", true).isValid()) {
          const [startDate, endDate] = cloendProps.label.split(" – ");
          cloendProps.label = `${moment(startDate, "MMMM DD").format("MMM D")} - ${endDate}`;
        } else if (
          moment(
            `${cloendProps.label} ${currentDate.year()}`,
            "dddd MMM DD YYYY",
          ).isValid()
        ) {
          cloendProps.label = `${currentDate.format("MMM D, YYYY")}`;
        }

        return CalendarToolbar({ ...cloendProps });
      },
      event: (props) =>
        customEventTile
          ? customEventTile({
              ...props,
              contextMenuOptions: options?.contextMenuOptions,
              onHover: onHover,
              onEventClick: options?.onEventClick,
              popOverComp: popOverComp,
              emptySlotMessage,
              showAvatar,
              showContextMenu,
              onSelectEvent: rest?.onSelectEvent,
            })
          : Event({
              ...props,
              contextMenuOptions: options?.contextMenuOptions,
              onHover: onHover,
              onEventClick: options?.onEventClick,
              popOverComp: popOverComp,
              emptySlotMessage,
              showAvatar,
              showContextMenu,
              onSelectEvent: rest?.onSelectEvent,
            }),
      ...(emptySlotMessage
        ? {
            dateCellWrapper: (props) => (
              <CustomDateCellWrapper {...props} events={events} />
            ),
          }
        : {}),
      // timeSlotWrapper: CustomTimeSlotWrapper,
      timeGutterHeader: ({ label }) => (
        <Box sx={{ fontSize: "0.9rem", paddingLeft: "4px", fontWeight: "600" }}>
          Time
        </Box>
      ),
      // timeGutterHeader: () => null, // Hides the time header
      // timeGutter: () => null,
      // ...customComps,
    }),

    [events],
  );

  const CustomDateCellWrapper = ({ value, children, events }) => {
    const hasEvents = events.some((event) => isSameDay(event.start, value));

    const isToday = isSameDay(new Date(), value);
    return (
      <Box className={`rbc-day-bg ${isToday ? "rbc-today" : ""}`}>
        {children}
        {!hasEvents && (
          <Typography
            className="d-flex align-items-center justify-content-center"
            sx={{
              height: "100%",
              fontSize: "0.8rem",
              color: "var(--palette-grey_text)",
            }}
          >
            {emptySlotMessage}
          </Typography>
        )}
      </Box>
    );
  };
  // for custom styling of event view
  const eventPropGetter = useCallback(eventStyling, []);

  // for hiding time from event detail on day and week view mode
  const formats = useMemo(
    () => ({
      eventTimeRangeFormat: () => {
        return "";
      },
      ...(removeTime ? { timeGutterFormat: () => "" } : {}), // Removes time from the left gutter
    }),
    [],
  );

  const clickRef = useRef(null);

  useEffect(() => {
    /**
     * What Is This?
     * This is to prevent a memory leak, in the off chance that you
     * teardown your interface prior to the timed method being called.
     */
    return () => {
      window.clearTimeout(clickRef?.current);
    };
  }, []);

  const onSelectSlot = useCallback((slotInfo) => {
    console.log("onSelectSlot::", slotInfo);
    /**
     * Here we are waiting 250 milliseconds (use what you want) prior to firing
     * our method. Why? Because both 'click' and 'doubleClick'
     * would fire, in the event of a 'doubleClick'. By doing
     * this, the 'click' handler is overridden by the 'doubleClick'
     * action.
     */
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      if (options && options.onSlotClick) {
        options.onSlotClick(slotInfo);
      }
    }, 250);
  }, []);

  const generateData = (events = []) => {
    let temp = (events || []).map((e) => ({
      ...e,
      // id: 1,
      // title:
      //   "Initial Assessment  Joseph Follow up  Joseph Follow up  Joseph Follow up",
      start: e.start ?? moment(e.startDate).toDate(),
      end: e.end ?? moment(e.endDate).toDate(),
      // start: moment().subtract(5, "hour").toDate(),
      // end: moment().add(5, "hour").toDate(),
      type: "task",
      // serviceTypeId: 1,
      // userId: 4,
    }));

    return temp;
  };
  const normalEvents = generateData(events);
  const processedEvents = groupOverlappingEvents(normalEvents);

  return (
    <Box className={styles.calendarContainer} ref={calendarRef}>
      <DragAndDropCalendar
        localizer={localizer}
        events={
          grouping && [Views.DAY, Views.WEEK].includes(selectedView)
            ? processedEvents
            : normalEvents
        }
        startAccessor="start"
        endAccessor="end"
        defaultView={defaultView}
        // views={{ month: true, week: customWeekView || true }}
        views={views}
        view={selectedView}
        onView={handleView}
        // timeslots={1}
        // step={1440}
        // popup
        tooltipAccessor={null}
        components={components}
        eventPropGetter={eventPropGetter}
        messages={messages}
        formats={formats}
        // dayLayoutAlgorithm="overlap"
        dayLayoutAlgorithm="no-overlap"
        // dayLayoutAlgorithm="stack"
        onSelectSlot={onSelectSlot}
        selectable
        doShowMoreDrillDown={false}
        // drag event method
        onEventDrop={options?.onEventDrop}
        draggableAccessor={stopDraggable}
        resizable={false}
        style={{
          background: "var(--palette-container)",
        }}
        onRangeChange={handleRange}
        // showMultiDayTimes
        customProp={{
          createContextMenu: options?.contextMenuOptions,
          emptySlotMessage,
          weekDayTaskState,
          onLoadMoreWeekDayTasks,
          ...options,
        }}
        {...rest}
      />
    </Box>
  );
};

export default UICalendar;
